/**
 * Visuels de « preuve » des diapositives de `/decouverte` : un aperçu de ce que
 * la promesse veut dire dans l'app (une feuille de brouillon vérifiée, de vraies
 * SKILLCARDS, un défi Live School, la frise de l'année, un bilan…).
 *
 * Purement décoratifs (`aria-hidden`) : le texte de la diapositive porte le sens.
 * Couleurs LITTÉRALES : l'accueil est toujours sombre (photo + bleu nuit), quel
 * que soit le thème clair/sombre de l'appareil.
 */

import { CardVerso, ScaledCard } from '@jeraisonne/ui/tool-cards';
import { cn } from '@jeraisonne/ui';
import { DECOUVERTE_CARDS } from './decouverte-cards';
import type { DecouverteListRow, DecouverteProof, DecouverteYearStop } from './decouverte-slides';

/** Le cadre de verre commun à ces visuels (repris par ceux de la fin de la démo). */
export const PROOF_PANEL =
  'rounded-2xl border border-white/15 bg-[#0a1a3f]/85 shadow-[0_18px_40px_-14px_rgba(0,0,0,0.7)] backdrop-blur-md';

export function DecouverteProofView({ proof }: { proof: DecouverteProof }) {
  return (
    <div aria-hidden className="pointer-events-none select-none" data-proof={proof.kind}>
      {proof.kind === 'sheet' && <ProofSheet />}
      {proof.kind === 'cards' && <ProofCards chip={proof.chip} />}
      {proof.kind === 'live' && <ProofLive />}
      {proof.kind === 'year' && <ProofYear badge={proof.badge} stops={proof.stops} />}
      {proof.kind === 'list' && <ProofList title={proof.title} rows={proof.rows} />}
    </div>
  );
}

// ─── Feuille de brouillon ─────────────────────────────────────────────────────

function ProofSheet() {
  return (
    <div className="w-[17.5rem] -rotate-[1.5deg] rounded-2xl bg-[#fbfcff] px-3 pb-2.5 pt-2 shadow-[0_18px_40px_-12px_rgba(0,0,0,0.65)] [background-image:linear-gradient(#e3ebf7_1px,transparent_1px),linear-gradient(90deg,#e3ebf7_1px,transparent_1px)] [background-size:14px_14px]">
      <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#5f7196]">
        Mon brouillon
      </p>
      <ul className="mt-1 space-y-0.5 border-l-2 border-[#f3a6a6] pl-0">
        <SheetLine mark="départ" text="BC² = AB² + AC²" />
        <SheetLine mark="✓" gain="+1" text="BC² = 36 + 64" tone="good" />
        <SheetLine mark="🛑" text="BC² = 110" tone="bad" note="36 + 64 = 100" />
        <SheetLine mark="✓✓" gain="+1" text="BC = 10" tone="goal" note="C’est la réponse !" />
      </ul>
    </div>
  );
}

function SheetLine({
  mark,
  gain,
  text,
  tone,
  note,
}: {
  mark: string;
  gain?: string;
  text: string;
  tone?: 'good' | 'bad' | 'goal';
  note?: string;
}) {
  return (
    <li
      className={cn(
        'flex items-baseline gap-2 rounded-md py-0.5 pl-1 pr-1.5',
        tone === 'bad' && 'bg-[#ffe9e9]',
        tone === 'goal' && 'bg-[#e4f8ee]',
      )}
    >
      <span
        className={cn(
          'w-12 shrink-0 text-right text-[11px] font-semibold',
          tone === 'good' || tone === 'goal' ? 'text-[#1f9d63]' : 'text-[#5f7196]',
          tone === 'bad' && 'text-[#d64545]',
        )}
      >
        {gain && <span className="mr-0.5 text-[10px] text-[#c79a26]">{gain}</span>}
        {mark}
      </span>
      <span className="font-['STIX_Two_Text',serif] text-[17px] leading-6 text-[#0f2f63]">
        {text}
      </span>
      {note && (
        <span
          className={cn(
            "ml-auto truncate font-['Comfortaa',sans-serif] text-xs font-bold leading-none",
            tone === 'bad' ? 'text-[#d64545]' : 'text-[#1f9d63]',
          )}
        >
          {note}
        </span>
      )}
    </li>
  );
}

// ─── SKILLCARDS ───────────────────────────────────────────────────────────────

const FAN = [
  { rot: '-rotate-[11deg]', pos: 'translate-x-[-62%] translate-y-[10%]', z: 'z-0' },
  { rot: 'rotate-0', pos: 'translate-x-[-50%]', z: 'z-10' },
  { rot: 'rotate-[11deg]', pos: 'translate-x-[-38%] translate-y-[10%]', z: 'z-0' },
] as const;

function ProofCards({ chip }: { chip?: string }) {
  // carte du milieu = Pythagore (la plus parlante), de part et d'autre Thalès et (a + b)²
  const order = [DECOUVERTE_CARDS[0], DECOUVERTE_CARDS[1], DECOUVERTE_CARDS[2]];
  return (
    <div className="relative h-[11.5rem] w-[19rem]">
      {order.map((card, i) => {
        if (!card) return null;
        const f = FAN[i]!;
        const side = i === 0 ? 'left-[22%]' : i === 1 ? 'left-1/2' : 'left-[78%]';
        return (
          <div
            key={card.id}
            className={cn('absolute top-0 w-[7.6rem]', side, f.pos, f.z)}
            data-testid="decouverte-card"
          >
            <div className={cn('origin-bottom drop-shadow-[0_14px_22px_rgba(0,0,0,0.55)]', f.rot)}>
              <ScaledCard>
                <CardVerso
                  cardId={card.id}
                  shortCode={card.shortCode}
                  title={card.title}
                  cardType={card.cardType}
                  levels={card.levels}
                  xpMax={card.xpMax}
                  rarity={card.rarity}
                  colorTheme="level"
                  domainLabel={card.domainLabel}
                  creature={card.creature}
                  content={{ sections: [{ kind: 'formula', latex: card.latex, variables: [] }] }}
                />
              </ScaledCard>
            </div>
          </div>
        );
      })}
      {chip && (
        <span className="absolute -bottom-1 left-1/2 z-20 -translate-x-1/2 whitespace-nowrap rounded-full bg-gradient-to-b from-[#f7d068] to-[#f4c64e] px-3 py-1 text-xs font-extrabold text-[#0a2e57] shadow-[0_4px_0_#c79a26]">
          {chip}
        </span>
      )}
    </div>
  );
}

// ─── Live School ──────────────────────────────────────────────────────────────

function ProofLive() {
  const rows = [
    { rank: '1', name: 'Toi', score: '980', me: true },
    { rank: '2', name: '•••••', score: '940', me: false },
    { rank: '3', name: '•••••', score: '910', me: false },
  ];
  return (
    <div className={cn(PROOF_PANEL, 'w-[17.5rem] p-3')}>
      <p className="flex items-center gap-2 text-[11px] font-extrabold uppercase tracking-[0.14em] text-white">
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full rounded-full bg-[#ff5d5d] opacity-75 motion-safe:animate-ping" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[#ff5d5d]" />
        </span>
        En direct · Live 3e
      </p>
      <ol className="mt-2 space-y-1">
        {rows.map((r) => (
          <li
            key={r.rank}
            className={cn(
              'flex items-center gap-2.5 rounded-xl px-2.5 py-1.5 text-sm',
              r.me ? 'bg-[#f7d068] text-[#0a2e57]' : 'bg-white/[0.07] text-white/80',
            )}
          >
            <span className="font-display w-4 font-extrabold">{r.rank}</span>
            <span className="flex-1 font-semibold">{r.name}</span>
            <span className="font-display font-bold tabular-nums">{r.score}</span>
          </li>
        ))}
      </ol>
      <p className="mt-2 text-center text-xs font-semibold text-[#f7d068]">
        🏆 À gagner : une SKILLCARD légendaire
      </p>
    </div>
  );
}

// ─── Frise de l'année ─────────────────────────────────────────────────────────

const STOP_STYLE: Record<DecouverteYearStop['state'], { dot: string; label: string }> = {
  done: { dot: 'bg-[#3ddc97] text-[#06331f]', label: '✓' },
  now: { dot: 'bg-[#2f7de0] text-white ring-4 ring-[#2f7de0]/30', label: '●' },
  next: { dot: 'bg-white/15 text-white/70', label: '' },
  goal: { dot: 'bg-[#f7d068] text-[#0a2e57]', label: '★' },
};

function ProofYear({ badge, stops }: { badge: string; stops: readonly DecouverteYearStop[] }) {
  return (
    <div className={cn(PROOF_PANEL, 'w-[19rem] px-3 pb-3 pt-2.5')}>
      <span className="inline-flex rounded-full bg-[#f7d068] px-2.5 py-0.5 text-[11px] font-extrabold text-[#0a2e57]">
        {badge}
      </span>
      <ol className="relative mt-3 grid grid-cols-4 gap-1.5">
        <span className="absolute left-[12%] right-[12%] top-[0.6rem] h-px bg-white/25" />
        {stops.map((s) => {
          const st = STOP_STYLE[s.state];
          return (
            <li key={s.month} className="relative flex flex-col items-center text-center">
              <span
                className={cn(
                  'flex h-5 w-5 items-center justify-center rounded-full text-[10px] font-extrabold',
                  st.dot,
                )}
              >
                {st.label}
              </span>
              <span className="mt-1.5 text-[10px] font-extrabold uppercase tracking-wider text-[#8ec5ff]">
                {s.month}
              </span>
              <span className="mt-0.5 text-[11px] font-semibold leading-tight text-white">
                {s.title}
              </span>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// ─── Liste (bilan, suivi, agenda) ─────────────────────────────────────────────

const TONE: Record<DecouverteListRow['tone'], string> = {
  good: 'bg-[#3ddc97]/15 text-[#7ef0bd]',
  warn: 'bg-[#ff8a5c]/15 text-[#ffb08f]',
  info: 'bg-[#2f7de0]/20 text-[#9cc8ff]',
  gold: 'bg-[#f7d068]/20 text-[#f7d068]',
};

function ProofList({ title, rows }: { title: string; rows: readonly DecouverteListRow[] }) {
  return (
    <div className={cn(PROOF_PANEL, 'w-[19rem] p-3')}>
      <p className="text-[11px] font-extrabold uppercase tracking-[0.14em] text-white/70">
        {title}
      </p>
      <ul className="mt-2 space-y-1.5">
        {rows.map((r) => (
          <li
            key={`${r.label}-${r.detail ?? ''}`}
            className="flex items-center gap-2.5 rounded-xl bg-white/[0.06] px-2.5 py-1.5"
          >
            <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-white/10 text-sm">
              {r.icon}
            </span>
            <span className="min-w-0 flex-1 truncate whitespace-nowrap text-sm font-semibold text-white">
              {r.label}
            </span>
            {r.detail && (
              <span
                className={cn(
                  'max-w-[60%] shrink-0 truncate rounded-full px-2 py-0.5 text-[11px] font-bold',
                  TONE[r.tone],
                )}
              >
                {r.detail}
              </span>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
